import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
  ViewEncapsulation
} from '@angular/core';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { InputNumberModule } from 'primeng/inputnumber';
import { AbstractControl, FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { CommonModule, NgIf } from '@angular/common';
import { ButtonModule } from 'primeng/button';
import { UserValidationService } from '../../services/user-validation.service';
import { User } from '../../models/users';
import { SharedDataService } from '../../services/shared-data.service';
import { Router } from '@angular/router';
import { MessageService } from 'primeng/api';
import { ToastModule } from 'primeng/toast';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { DialogModule } from 'primeng/dialog';
import { takeUntil } from 'rxjs/operators';
import { firstValueFrom, Subject } from 'rxjs';
import { DeviceInfoService } from '../../services/device-info.service';
import { TurnstileComponent } from '../../shared/components/turnstile/turnstile.component';
import { encryptAES } from '../../utils/crypto/aes-encryption.util';
import { environment } from '../../environments/enviroment';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { LanguageSwitcherComponent } from '../../shared/components/language-switcher/language-switcher.component';

interface FieldState {
  touched: boolean;
  focused: boolean;
  valid: boolean;
}

interface DeviceInfo {
  uuid: string;
  name: string;
}

@Component({
  selector: 'app-login',
  imports: [
    FormsModule,
    InputGroupModule,
    InputGroupAddonModule,
    InputTextModule,
    SelectModule,
    InputNumberModule,
    ReactiveFormsModule,
    CommonModule,
    NgIf,
    ButtonModule,
    ToastModule,
    ProgressSpinnerModule,
    DialogModule,
    TurnstileComponent,
    TranslateModule,
    LanguageSwitcherComponent
  ],
  providers: [MessageService],
  templateUrl: './login.component.html',
  styleUrl: './login.component.scss',
  standalone: true,
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LoginComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('emailInput') emailInput!: ElementRef<HTMLInputElement>;

  public readonly turnstileSiteKey = environment.turnstile.siteKey;
  protected turnstileToken: string | null = null;

  disableTurnstile: boolean = false;

  // Form definition
  loginForm: FormGroup;

  // Component state
  showPassword: boolean = false;
  isLoading: boolean = false;
  isResending: boolean = false;
  showResendDialog: boolean = false;
  unverifiedEmail: string = '';
  isInitializing: boolean = true;

  // Session conflict dialog state
  showSessionConflictDialog: boolean = false;
  isCleaningUserData: boolean = false;
  conflictEmail: string = '';

  // Device info cache
  private deviceInfo: DeviceInfo | null = null;

  // Field states
  fieldStates: { [key: string]: FieldState } = {
    email: { touched: false, focused: false, valid: false },
    password: { touched: false, focused: false, valid: false }
  };

  // Subject for handling component cleanup
  private destroy$ = new Subject<void>();

  @ViewChild(TurnstileComponent) turnstileComponent!: TurnstileComponent;
  constructor(
    private fb: FormBuilder,
    private userValidationService: UserValidationService,
    private sharedDataService: SharedDataService,
    private router: Router,
    private messageService: MessageService,
    private changeDetectorRef: ChangeDetectorRef,
    private deviceInfoService: DeviceInfoService,
    private translate: TranslateService,
  ) {
    // Initialize form with validators
    this.loginForm = this.fb.group({
      email: ['', [Validators.required, Validators.email, Validators.maxLength(100)]],
      password: ['', [Validators.required, Validators.minLength(6), Validators.maxLength(50)]],
      rememberMe: [false]
    });
  }


  /**
   * Handles the event when the Turnstile CAPTCHA is resolved
   * @param token
   */
  onTurnstileResolved(token: string | null): void {
    this.turnstileToken = token;
  }

  async ngOnInit(): Promise<void> {
    try {
      const agent = this.sharedDataService.getAgent();

      if (agent && this.userValidationService.hasAuthenticatedSession()) {
        this.router.navigate(['/workspace-selection']);
        return;
      }

      if (agent && !this.userValidationService.hasAuthenticatedSession()) {
        this.sharedDataService.clearUser();
      }

      // Initialize device info first
      await this.initializeDeviceInfo();

      // Read Turnstile feature flag from backend to decide whether it is required
      await this.loadTurnstileConfig();

      // Load remembered email if exists
      this.loadRememberedEmail();

      // Watch form value changes to update validation state
      this.setupFormValidationListeners();
    } catch (error) {
      console.error('Error during component initialization:', error);
      this.messageService.add({
        severity: 'warn',
        summary: this.translate.instant('login.toast.init.summary'),
        detail: this.translate.instant('login.toast.init.detail'),
        life: 3000
      });
    } finally {
      this.isInitializing = false;
      this.changeDetectorRef.markForCheck();
    }
  }

  ngAfterViewInit(): void {
    // Focus the email field after view initialization and device info is loaded
    setTimeout(() => {
      if (!this.isInitializing) {
        this.emailInput?.nativeElement?.focus();
        this.changeDetectorRef.markForCheck();
      }
    }, 100);
  }

  ngOnDestroy(): void {
    // Cleanup subscriptions
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Initialize device information
   */
  private async initializeDeviceInfo(): Promise<void> {
    try {
      this.deviceInfo = await this.deviceInfoService.initializeDeviceInfo();
    } catch (error) {
      console.error('Failed to initialize device info:', error);
      // Fallback to sync methods if available
      this.deviceInfo = {
        uuid: this.deviceInfoService.getDeviceUUIDSync() || 'unknown',
        name: this.deviceInfoService.getDeviceNameSync() || 'unknown'
      };
    }
  }

  private async loadTemporaryTurnstileConfig(): Promise<void> {
    try {
      const config = await firstValueFrom(this.userValidationService.getTemporaryTurnstileConfig());
      this.disableTurnstile = config?.enabled === false;
      this.turnstileToken = this.disableTurnstile ? 'disable' : null;
    } catch (error) {
      // TEMPORARY HOTFIX: do not block logins if endpoint is unavailable.
      console.warn('Turnstile temporary config unavailable. Keeping Turnstile disabled provisionally.', error);
      this.disableTurnstile = true;
      this.turnstileToken = 'disable';
    }
  }

  private async loadTurnstileConfig(): Promise<void> {
    try {
      const config = await firstValueFrom(
        this.userValidationService.getTurnstileConfig(environment.platformNameOrchGT)
      );

      // enabled=false means Turnstile is not required for login.
      this.disableTurnstile = config?.enabled === false;
      this.turnstileToken = this.disableTurnstile ? 'disable' : null;
    } catch (error) {
      // Fail closed: if config cannot be fetched, keep Turnstile required.
      console.error('Error loading Turnstile config:', error);
      this.disableTurnstile = false;
      this.turnstileToken = null;
    }
  }

  /**
   * Setup form validation listeners
   */
  private setupFormValidationListeners(): void {
    Object.keys(this.fieldStates).forEach(fieldName => {
      const control = this.loginForm.get(fieldName);
      if (control) {
        control.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
          this.updateFieldValidity(fieldName, control);
          this.changeDetectorRef.markForCheck();
        });
      }
    });
  }

  /**
   * Update field validity state
   */
  private updateFieldValidity(fieldName: string, control: AbstractControl): void {
    if (this.fieldStates[fieldName]) {
      this.fieldStates[fieldName].valid = control.valid;
    }
  }

  /**
   * Handle form submission with async device info
   */
  async onSubmit(): Promise<void> {
    // Prevent submission if still initializing
    if (this.isInitializing || this.isLoading) {
      return;
    }

    if (!this.turnstileToken && !this.disableTurnstile) {
      this.messageService.add({
        severity: 'warn',
        summary: this.translate.instant('login.toast.captcha.summary'),
        detail: this.translate.instant('login.toast.captcha.detail'),
        life: 4000
      });
      return;
    }

    // Mark all fields as touched
    this.markAllAsTouched();

    if (this.loginForm.valid) {
      const { email, password, rememberMe } = this.loginForm.value;
      this.isLoading = true;
      this.changeDetectorRef.markForCheck();

      try {
        // Save email if remember me is checked
        this.handleRememberMe(email, rememberMe);

        // Ensure device info is available
        let deviceInfo = this.deviceInfo;
        if (!deviceInfo) {
          deviceInfo = await this.deviceInfoService.initializeDeviceInfo();
          this.deviceInfo = deviceInfo;
        }

        this.userValidationService.login2(
          encryptAES(email),
          encryptAES(password),
          encryptAES(environment.platformNameOrchGT),
          deviceInfo.uuid,
          deviceInfo.name,
          this.turnstileToken ?? 'disable'
        )
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: (resp: any) => {
              if (this.tryHandleModernAuthResponse(resp)) {
                return;
              }

              // Determinar si requiere OTP. El backend lo informa explicitamente.
              const requiresOtp = !!resp?.requiresOtp;

              if (requiresOtp) {
                // No finalizar sesión completa aún: redirigir a OTP screen
                this.isLoading = false;
                this.changeDetectorRef.markForCheck();
                this.router.navigate(['/otp-auth']);
                return;
              }

              // Flujo normal: extraer user y continuar
              const user: User = resp?.clientAgent?.user;
              if (user) {
                this.handleLoginSuccess(user);
                setTimeout(() => {
                  localStorage.setItem('worksel', 'pending');
                  this.router.navigate(['/workspace-selection']);
                }, 2000);
              } else {
                // Fallback: si no viene clientAgent.user intentar manejar error
                this.isLoading = false;
                this.messageService.add({ severity: 'error', summary: this.translate.instant('login.toast.error.summary'), detail: this.translate.instant('login.toast.invalidResponse') });
              }
            },
            error: (error: any) => {
              this.handleLoginError(error, email);
              if (!this.disableTurnstile && this.turnstileComponent) {
                this.turnstileComponent.resetWidget();
                this.turnstileToken = null;
              }
              if (error.status !== 409) {
                setTimeout(() => {
                  localStorage.setItem('worksel', 'error');
                  this.router.navigate(['/layout']);
                }, 1000);
              }
            }
          });

      } catch (deviceError) {
        console.error('Device info error during login:', deviceError);
        this.handleLoginError(deviceError, email);
      }
    } else {
      this.focusFirstInvalidField();
    }
  }



  /**
   * Mark all form fields as touched
   */
  markAllAsTouched(): void {
    Object.keys(this.loginForm.controls).forEach(controlName => {
      const control = this.loginForm.get(controlName);
      if (control) {
        control.markAsTouched();
        if (this.fieldStates[controlName]) {
          this.fieldStates[controlName].touched = true;
        }
      }
    });
    this.changeDetectorRef.markForCheck();
  }

  /**
   * Focus first invalid field
   */
  private focusFirstInvalidField(): void {
    const emailControl = this.loginForm.get('email');
    const passwordControl = this.loginForm.get('password');

    setTimeout(() => {
      if (emailControl?.invalid) {
        this.emailInput?.nativeElement.focus();
        this.onFieldFocus('email');
      } else if (passwordControl?.invalid) {
        const passwordElement = document.getElementById('password');
        passwordElement?.focus();
        this.onFieldFocus('password');
      }
    }, 100);
  }

  /**
   * Handle successful login response
   */
  private handleLoginSuccess(user: User): void {
    this.isLoading = false;

    this.messageService.add({
      severity: 'success',
      summary: this.translate.instant('login.toast.success.summary'),
      detail: this.translate.instant('login.toast.success.detail', { username: user.username }),
      life: 3000
    });
    this.sharedDataService.setAgent(user);

    localStorage.setItem('status_agent', user.status);
    this.changeDetectorRef.markForCheck();
  }

  private tryHandleModernAuthResponse(resp: any): boolean {
    if (resp?.requiresCompanySelection || resp?.requiresOtp || resp?.access_token) {
      this.handleAuthResponse(resp);
      return true;
    }

    return false;
  }

  private handleAuthResponse(resp: any): void {
    if (resp?.requiresCompanySelection) {
      this.isLoading = false;
      localStorage.setItem('worksel', 'pending');
      this.changeDetectorRef.markForCheck();
      this.router.navigate(['/workspace-selection']);
      return;
    }

    if (resp?.requiresOtp) {
      this.isLoading = false;
      this.changeDetectorRef.markForCheck();
      this.router.navigate(['/otp-auth']);
      return;
    }

    if (resp?.access_token) {
      this.finalizeAuthenticatedFlow();
      return;
    }

    const user: User = resp?.clientAgent?.user;
    if (user) {
      this.handleLoginSuccess(user);
      setTimeout(() => {
        localStorage.setItem('worksel', 'pending');
        this.router.navigate(['/workspace-selection']);
      }, 2000);
      return;
    }

    this.isLoading = false;
    this.messageService.add({
      severity: 'error',
      summary: this.translate.instant('login.toast.error.summary'),
      detail: this.translate.instant('login.toast.invalidResponse')
    });
    this.changeDetectorRef.markForCheck();
  }

  private finalizeAuthenticatedFlow(): void {
    this.userValidationService.loadAuthenticatedUserProfile()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (user) => {
          this.handleLoginSuccess(user);
          setTimeout(() => {
            localStorage.setItem('worksel', 'pending');
            this.router.navigate(['/workspace-selection']);
          }, 1200);
        },
        error: (error) => {
          console.error('Error loading authenticated profile:', error);
          this.isLoading = false;
          this.messageService.add({
            severity: 'error',
            summary: this.translate.instant('login.toast.sessionError.summary'),
            detail: this.translate.instant('login.toast.sessionError.detail')
          });
          this.changeDetectorRef.markForCheck();
        }
      });
  }

  /**
   * Handle login error responses with better categorization
   */
  private handleLoginError(error: any, email: string): void {
    this.isLoading = false;

    if (error.status === 403 && error.error?.reason === 'unverified') {
      this.unverifiedEmail = email;
      this.showResendDialog = true;
    } else if (error.status === 401) {
      this.messageService.add({
        severity: 'error',
        summary: this.translate.instant('login.toast.authError.summary'),
        detail: this.translate.instant('login.toast.authError.detail'),
        life: 5000
      });
      // Clear password field for security
      this.loginForm.patchValue({ password: '' });
      this.fieldStates['password'].touched = false;
    } else if (error.status === 429) {
      this.messageService.add({
        severity: 'warn',
        summary: this.translate.instant('login.toast.tooManyAttempts.summary'),
        detail: this.translate.instant('login.toast.tooManyAttempts.detail'),
        life: 8000
      });
    } else if (error.status === 0 || error.status >= 500) {
      this.messageService.add({
        severity: 'error',
        summary: this.translate.instant('login.toast.serverError.summary'),
        detail: this.translate.instant('login.toast.serverError.detail'),
        life: 6000
      });
    } else if (error.status === 409) {
      // Session conflict - user is logged in on another device
      this.conflictEmail = email;
      this.showSessionConflictDialog = true;

      this.messageService.add({
        severity: 'warn',
        summary: this.translate.instant('login.toast.sessionDetected.summary'),
        detail: this.translate.instant('login.toast.sessionDetected.detail'),
        life: 6000
      });
    } else {
      this.messageService.add({
        severity: 'error',
        summary: this.translate.instant('login.toast.unexpectedError.summary'),
        detail: this.translate.instant('login.toast.unexpectedError.detail'),
        life: 5000
      });
    }

    this.changeDetectorRef.markForCheck();
  }

  /**
   * Handle "remember me" functionality
   */
  private handleRememberMe(email: string, rememberMe: boolean): void {
    if (rememberMe) {
      localStorage.setItem('rememberedEmail', email);
    } else {
      localStorage.removeItem('rememberedEmail');
    }
  }

  /**
   * Load remembered email from localStorage
   */
  private loadRememberedEmail(): void {
    const rememberedEmail = localStorage.getItem('rememberedEmail');
    if (rememberedEmail) {
      this.loginForm.patchValue({
        email: rememberedEmail,
        rememberMe: true
      });
      this.fieldStates['email'].touched = true;
      this.changeDetectorRef.markForCheck();
    }
  }

  /**
   * Check if a field has errors and should show them
   */
  hasFieldError(fieldName: string): boolean {
    const control = this.loginForm.get(fieldName);
    return !!control && control.invalid && (this.fieldStates[fieldName]?.touched || control.touched);
  }

  /**
   * Get error message for email field
   */
  getEmailErrorMessage(): string {
    const control = this.loginForm.get('email');
    if (!control?.errors) return '';

    if (control.hasError('required')) {
      return this.translate.instant('login.email.errorRequired');
    }
    if (control.hasError('email')) {
      return this.translate.instant('login.email.errorInvalid');
    }
    if (control.hasError('maxlength')) {
      return this.translate.instant('login.email.errorMaxLength');
    }

    return this.translate.instant('login.email.errorGeneric');
  }

  /**
   * Get error message for password field
   */
  getPasswordErrorMessage(): string {
    const control = this.loginForm.get('password');
    if (!control?.errors) return '';

    if (control.hasError('required')) {
      return this.translate.instant('login.password.errorRequired');
    }
    if (control.hasError('minlength')) {
      const requiredLength = control.getError('minlength').requiredLength;
      return this.translate.instant('login.password.errorMinLength', { length: requiredLength });
    }
    if (control.hasError('maxlength')) {
      return this.translate.instant('login.password.errorMaxLength');
    }

    return this.translate.instant('login.password.errorGeneric');
  }

  /**
   * Handle field focus event
   */
  onFieldFocus(field: string): void {
    if (this.fieldStates[field]) {
      this.fieldStates[field].focused = true;
      this.changeDetectorRef.markForCheck();
    }
  }

  /**
   * Handle field blur event
   */
  onFieldBlur(field: string): void {
    if (this.fieldStates[field]) {
      this.fieldStates[field].focused = false;
      this.fieldStates[field].touched = true;
      this.loginForm.get(field)?.markAsTouched();
      this.changeDetectorRef.markForCheck();
    }
  }

  /**
   * Toggle password visibility
   */
  togglePasswordVisibility(): void {
    this.showPassword = !this.showPassword;
    setTimeout(() => {
      document.getElementById('password')?.focus();
    }, 10);
    this.changeDetectorRef.markForCheck();
  }

  /**
   * Handle verification resend request with better error handling
   */
  async resendVerification(): Promise<void> {
    if (!this.unverifiedEmail || this.isResending) return;

    this.isResending = true;
    this.changeDetectorRef.markForCheck();

    try {
      // TODO: Replace with actual service call
      // await this.userValidationService.resendVerification(this.unverifiedEmail);

      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1500));

      this.isResending = false;
      this.showResendDialog = false;

      this.messageService.add({
        severity: 'success',
        summary: this.translate.instant('login.toast.emailSent.summary'),
        detail: this.translate.instant('login.toast.emailSent.detail'),
        life: 5000
      });

    } catch (error) {
      this.isResending = false;

      this.messageService.add({
        severity: 'error',
        summary: this.translate.instant('login.toast.sendError.summary'),
        detail: this.translate.instant('login.toast.sendError.detail'),
        life: 5000
      });
    }

    this.changeDetectorRef.markForCheck();
  }

  /**
   * Handle session conflict by cleaning user data on other devices
   */
  async cleanUserDataAndLogin(): Promise<void> {
    if (!this.conflictEmail || this.isCleaningUserData) return;

    this.isCleaningUserData = true;
    this.changeDetectorRef.markForCheck();

    try {
      // Call clean_user_data service
      await this.userValidationService.clean_user_data(
        this.conflictEmail,
        this.loginForm.value.password
      ).pipe(takeUntil(this.destroy$)).toPromise();

      this.messageService.add({
        severity: 'success',
        summary: this.translate.instant('login.toast.sessionCleaned.summary'),
        detail: this.translate.instant('login.toast.sessionCleaned.detail'),
        life: 5000
      });

      // Close the dialog
      this.showSessionConflictDialog = false;
      this.isCleaningUserData = false;

      // Optionally auto-retry login after cleaning
      const { email, password } = this.loginForm.value;
      if (email && password) {
        // Small delay to ensure cleanup is processed
        setTimeout(() => {
          this.attemptLoginAfterCleanup(email, password);
        }, 1000);
      }

    } catch (error) {
      this.isCleaningUserData = false;
      this.showSessionConflictDialog = false;

      console.error('Error cleaning user data:', error);

      this.messageService.add({
        severity: 'error',
        summary: this.translate.instant('login.toast.cleanError.summary'),
        detail: this.translate.instant('login.toast.cleanError.detail'),
        life: 5000
      });
    }

    this.changeDetectorRef.markForCheck();
  }

  /**
   * Attempt login after cleaning user data
   */
  private async attemptLoginAfterCleanup(email: string, password: string): Promise<void> {
    try {
      // Ensure device info is available
      let deviceInfo = this.deviceInfo;
      if (!deviceInfo) {
        deviceInfo = await this.deviceInfoService.initializeDeviceInfo();
        this.deviceInfo = deviceInfo;
      }

      this.isLoading = true;
      this.changeDetectorRef.markForCheck();

      // Attempt login again
      this.userValidationService.login2(
        encryptAES(email),
        encryptAES(password),
        encryptAES(environment.platformNameOrchGT),
        deviceInfo.uuid,
        deviceInfo.name,
        this.turnstileToken ?? 'disable'
      )
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (resp: any) => {
            this.handleAuthResponse(resp);
          },
          error: (error: any) => {
            this.handleLoginError(error, email);

            if (!this.disableTurnstile) {
              if (this.turnstileComponent) {
                this.turnstileComponent.resetWidget();
              }
              this.turnstileToken = null;
            }
            setTimeout(() => {
              localStorage.setItem('worksel', 'error');
              this.router.navigate(['/layout']);
            }, 1000);
          }
        });

    } catch (error) {
      this.isLoading = false;
      console.error('Error during login retry after cleanup:', error);

      this.messageService.add({
        severity: 'error',
        summary: this.translate.instant('login.toast.retryError.summary'),
        detail: this.translate.instant('login.toast.retryError.detail'),
        life: 5000
      });

      this.changeDetectorRef.markForCheck();
    }
  }

  /**
   * Cancel session conflict dialog
   */
  cancelSessionConflict(): void {
    this.showSessionConflictDialog = false;
    this.conflictEmail = '';
    this.changeDetectorRef.markForCheck();
  }
}
